import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { ISelectValue } from 'constants/globalTypes'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllOperationPlansSingleResponse,
  IGetSingleOperationPlanDashboardResponse,
  IGetSingleOperationPlanResponse,
  IOperatinoPlanPostPatchRequest,
  IProcessBindingConfigRequest,
  IProcessBindingConfigResponse,
  ITaskCycleBindingConfigRequest,
  ITaskCycleBindingConfigResponse,
  IWorkflowBindingConfigRequest,
  IWorkflowBindingConfigResponse,
} from 'types/api/operationsPlansApi.interface'
import { currencies } from 'pages/Companies/AddCompany'
import {
  advanceRuleList,
  executableTypeList,
  boardVisibilityOptions,
  horizonTypeOptions,
  priorityOptions,
  workflowCriteriaList,
  zoneLevels,
} from 'pages/Operations/Plans/plansData'
import {
  IOperationPlanState,
  IPlansSliceInitalState,
  ITaskCycleBindingConfigState,
  IProcessBindingConfigState,
  IWorkflowBindingConfigState,
  IPhaseState,
} from 'types/operationPlans.interface'
import { baseUrl } from 'utils/axios'
import { getParams, handleErrors } from 'utils/helpers'

const findOption = (options: { label: string; value: number }[], value: number) =>
  options.find((o) => o.value === value)

const formatBindingConfigFromResponse = (
  executableType: number,
  bindingConfig:
    | IWorkflowBindingConfigResponse
    | IProcessBindingConfigResponse
    | ITaskCycleBindingConfigResponse
    | null
    | undefined,
):
  | IWorkflowBindingConfigState
  | IProcessBindingConfigState
  | ITaskCycleBindingConfigState
  | null => {
  if (!bindingConfig) return null

  if (executableType === 2) {
    const binding = bindingConfig as IWorkflowBindingConfigResponse
    return {
      startDate: binding.startDate ? new Date(binding.startDate) : null,
      criteria: binding.criteria ? findOption(workflowCriteriaList, binding.criteria) : null,
      department: binding.department
        ? { label: binding.department.name, value: binding.department.id }
        : null,
      items:
        binding.items?.map((item) => ({
          workflowTemplateItem: {
            label: item.workflowTemplateItem.taskTemplate.name,
            value: item.workflowTemplateItem.id,
          },
          location: item.location ? { label: item.location.name, value: item.location.id } : null,
          componentValues:
            item.componentValues?.map((component) => ({
              taskComponent: {
                label: component.taskComponent.name,
                value: component.taskComponent.id,
              },
              value: component.value,
            })) ?? [],
          toolComponentValues:
            item.toolComponentValues?.map((component) => ({
              taskTemplateToolTypeComponent: {
                label: component.toolTypeComponent.name,
                value: component.toolTypeComponent.id,
              },
              toolComponent: {
                label: component.toolTypeComponent.toolTypeComponent.name,
                value: component.toolComponentId,
              },
              amount: component.amount,
            })) ?? [],
        })) ?? [],
    }
  }

  if (executableType === 1) {
    const binding = bindingConfig as ITaskCycleBindingConfigResponse
    return {
      startDate: binding.startDate ? new Date(binding.startDate) : null,
      parallelismTarget: binding.parallelismTarget,
      department: binding.department
        ? { label: binding.department.name, value: binding.department.id }
        : null,
      componentsData:
        binding.componentsData?.map((component) => ({
          taskCycleTemplateComponent: {
            label: component.taskCycleTemplateComponent.name,
            value: component.taskCycleTemplateComponent.id,
          },
          componentType: component.taskCycleTemplateComponent.component,
          value: component.value,
        })) ?? [],
    }
  }

  if (executableType === 3) {
    const binding = bindingConfig as IProcessBindingConfigResponse
    return {
      startDate: binding.startDate ? new Date(binding.startDate) : null,
      location: binding.location
        ? {
            label:
              (binding.location as { name?: string; label?: string }).name ??
              binding.location.label,
            value:
              (binding.location as { id?: number; value?: number }).id ?? binding.location.value,
          }
        : null,
      department: binding.department
        ? { label: binding.department.name, value: binding.department.id }
        : null,
      componentsData:
        binding.componentsData?.map((component) => ({
          processTemplateComponent: {
            label: component.processTemplateComponent.name,
            value: component.processTemplateComponent.id,
          },
          componentType: component.processTemplateComponent.component,
          value: component.value,
        })) ?? [],
    }
  }

  return null
}

const initialState: IPlansSliceInitalState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singlePlan: null,
  dashboard: null,
}

export const activateOperationPlan = createAsyncThunk(
  'operationPlans/activateOperationPlan',
  async ({ id, single }: { id: number; single: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/activate`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const cancelOperationPlan = createAsyncThunk(
  'operationPlans/cancelOperationPlan',
  async ({ id, single }: { id: number; single?: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/cancel`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const pauseOperationPlan = createAsyncThunk(
  'operationPlans/pauseOperationPlan',
  async ({ id, single }: { id: number; single?: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/pause`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const completeOperationPlan = createAsyncThunk(
  'operationPlans/completeOperationPlan',
  async ({ id, single }: { id: number; single?: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/complete`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const resumeOperationPlan = createAsyncThunk(
  'operationPlans/resumeOperationPlan',
  async ({ id, single }: { id: number; single?: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/resume`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const activateOperationPlanPhase = createAsyncThunk(
  'operationPlans/cancelOperationPlanPhase',
  async ({ planId, phaseId }: { planId: number; phaseId: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `aim-operations/plan/${planId}/phases/${phaseId}/activate`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const completeOperationPlanPhase = createAsyncThunk(
  'operationPlans/completeOperationPlanPhase',
  async ({ planId, phaseId }: { planId: number; phaseId: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `aim-operations/plan/${planId}/phases/${phaseId}/complete`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const skipOperationPlanPhase = createAsyncThunk(
  'operationPlans/skipOperationPlanPhase',
  async ({ planId, phaseId }: { planId: number; phaseId: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `aim-operations/plan/${planId}/phases/${phaseId}/skip`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationPlans = createAsyncThunk(
  'operationPlans/getOperationPlans',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllOperationPlansSingleResponse>> =
        await baseUrl.get('aim-operations/plan', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationPlanDashboard = createAsyncThunk(
  'operationPlans/getOperationPlanDashboard',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetSingleOperationPlanDashboardResponse> = await baseUrl.get(
        `aim-operations/plan/${id}/dashboard`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleOperationPlan = createAsyncThunk(
  'operationPlans/getSingleOperationPlan',
  async (id: string, thunkAPI) => {
    try {
      const {
        data,
      }: AxiosResponse<
        IGetSingleOperationPlanResponse<
          | IWorkflowBindingConfigResponse
          | IProcessBindingConfigResponse
          | ITaskCycleBindingConfigResponse
        >
      > = await baseUrl.get('aim-operations/plan/' + id)
      const planState: IOperationPlanState = {
        name: data.name,
        code: data.code,
        status: data.status,
        site: data.site ? { label: data.site.name, value: data.site.id } : null,
        department: data.department
          ? { label: data.department.name, value: data.department.id }
          : null,
        owner: data.owner
          ? {
              label: `${data.owner.firstName} ${data.owner.lastName}`,
              value: data.owner.id,
            }
          : null,
        zoneScopeLevel: findOption(zoneLevels, data.zoneScopeLevel),
        zoneScopeIds: data.zoneScopeIds.map((zoneId) => ({
          label: String(zoneId),
          value: zoneId,
        })),
        priority: findOption(priorityOptions, data.priority),
        objective: data.objective,
        targetUnit: data.targetUnit
          ? { label: data.targetUnit.name, value: data.targetUnit.id }
          : null,
        targetValue: data.targetValue,
        targetBudgetCost: data.targetBudgetCost,
        targetCurrencyCode: data.targetCurrencyCode
          ? ({
              label:
                currencies.find((c) => c.value === data.targetCurrencyCode)?.label ??
                data.targetCurrencyCode.toString(),
              value: data.targetCurrencyCode,
            } as unknown as ISelectValue)
          : null,
        dueAt: data.dueAt ? new Date(data.dueAt) : null,
        boardVisibility: findOption(boardVisibilityOptions, data.boardVisibility),
        autoPauseOnL3Plus: data.autoPauseOnL3Plus,
        phases: data.phases.map((phase) => ({
          id: phase.id,
          name: phase.name,
          status: phase.status,
          sequence: phase.sequence,
          executableType: executableTypeList.find((o) => o.value === phase.executableType),
          advanceRule: advanceRuleList.find((o) => o.value === phase.advanceRule),
          workflowTemplate: phase.workflowTemplate
            ? { label: phase.workflowTemplate.name, value: phase.workflowTemplate.id }
            : null,
          processTemplate: phase.processTemplate
            ? { label: phase.processTemplate.name, value: phase.processTemplate.id }
            : null,
          taskCycleTemplate: phase.taskCycleTemplate
            ? { label: phase.taskCycleTemplate.name, value: phase.taskCycleTemplate.id }
            : null,
          bindingConfig: formatBindingConfigFromResponse(phase.executableType, phase.bindingConfig),
        })),
        horizon: {
          horizonType: data.horizon
            ? findOption(horizonTypeOptions, data.horizon.horizonType)
            : null,
          schedulerRunFreqMin: data.horizon?.schedulerRunFreqMin ?? null,
          replenishThreshold: data.horizon?.replenishThreshold ?? null,
          maxQueueSize: data.horizon?.maxQueueSize ?? null,
        },
      }
      return planState
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const formatBindingConfigForRequest = (
  phase: IPhaseState<
    IWorkflowBindingConfigState | IProcessBindingConfigState | ITaskCycleBindingConfigState
  >,
):
  | IWorkflowBindingConfigRequest
  | IProcessBindingConfigRequest
  | ITaskCycleBindingConfigRequest => {
  try {
    if (phase.executableType?.value == 2) {
      const binding = phase.bindingConfig as IWorkflowBindingConfigState
      return {
        startDate: binding.startDate?.toISOString(),
        criteria: binding.criteria?.value as 1 | 2,
        departmentId: binding.department?.value,
        items: binding.items?.map((item) => ({
          workflowTemplateItemId: item.workflowTemplateItem?.value,
          locationId: item.location?.value,
          componentValues: item.componentValues?.map((component) => ({
            taskComponentId: component.taskComponent?.value,
            value: component.value,
          })),
          toolComponentValues: item.toolComponentValues?.map((component) => ({
            taskTemplateToolTypeComponentId: component.taskTemplateToolTypeComponent?.value,
            toolComponentId: component.toolComponent?.value,
            amount: component.amount,
          })),
        })),
      }
    }

    if (phase.executableType?.value == 1) {
      const binding = phase.bindingConfig as ITaskCycleBindingConfigState
      return {
        startDate: binding.startDate?.toISOString(),
        parallelismTarget: binding.parallelismTarget,
        departmentId: binding.department?.value,
        componentsData: binding.componentsData?.map((component) => ({
          taskCycleTemplateComponentId: component.taskCycleTemplateComponent?.value,
          value: component.value,
        })),
      }
    }

    if (phase.executableType?.value == 3) {
      const binding = phase.bindingConfig as IProcessBindingConfigState
      return {
        startDate: binding.startDate?.toISOString(),
        locationId: binding.location?.value,
        departmentId: binding.department?.value,
        componentsData: binding.componentsData?.map((component) => ({
          processTemplateComponentId: component.processTemplateComponent?.value,
          value: component.value,
        })),
      }
    }
  } catch (error) {
    console.log(error)
  }
}

export const addOperationPlan = createAsyncThunk(
  'operationPlans/addOperationPlan',
  async (state: IOperationPlanState, thunkAPI) => {
    const req: IOperatinoPlanPostPatchRequest<
      | IWorkflowBindingConfigRequest
      | IProcessBindingConfigRequest
      | ITaskCycleBindingConfigRequest
      | null
    > = {
      name: state.name,
      code: state.code,
      siteId: state.site?.value,
      departmentId: state.department?.value,
      ownerId: state.owner?.value,
      zoneScopeLevel: state.zoneScopeLevel?.value,
      zoneScopeIds: state.zoneScopeIds?.map((id) => id?.value),
      priority: state.priority?.value,
      objective: state.objective,
      targetUnitId: state.targetUnit?.value,
      targetValue: state.targetValue,
      targetBudgetCost: state.targetBudgetCost,
      targetCurrencyCode: state.targetCurrencyCode?.value as unknown as string,
      dueAt: state.dueAt?.toISOString() || null,
      boardVisibility: state.boardVisibility?.value,
      autoPauseOnL3Plus: state.autoPauseOnL3Plus,
      horizon: state.horizon?.horizonType
        ? {
            horizonType: state.horizon.horizonType?.value,
            schedulerRunFreqMin: state.horizon?.schedulerRunFreqMin,
            replenishThreshold: state.horizon?.replenishThreshold,
            maxQueueSize: state.horizon?.maxQueueSize,
          }
        : null,
      phases: state.phases?.map((phase) => ({
        name: phase.name,
        sequence: phase.sequence,
        executableType: phase.executableType?.value,
        advanceRule: phase.advanceRule?.value,
        workflowTemplateId: phase.workflowTemplate?.value,
        processTemplateId: phase.processTemplate?.value,
        taskCycleTemplateId: phase.taskCycleTemplate?.value,
        bindingConfig: formatBindingConfigForRequest(phase),
      })),
    }

    try {
      const resp: AxiosResponse = await baseUrl.post('aim-operations/plan', req)
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editOperationPlan = createAsyncThunk(
  'operationPlans/editOperationPlan',
  async ({ id, state }: { id: string; state: IOperationPlanState }, thunkAPI) => {
    const req: IOperatinoPlanPostPatchRequest<
      IWorkflowBindingConfigRequest | IProcessBindingConfigRequest | ITaskCycleBindingConfigRequest
    > = {
      name: state.name,
      code: state.code,
      siteId: state.site?.value,
      departmentId: state.department?.value,
      ownerId: state.owner?.value,
      zoneScopeLevel: state.zoneScopeLevel?.value,
      zoneScopeIds: state.zoneScopeIds?.map((id) => id?.value),
      priority: state.priority?.value,
      objective: state.objective,
      targetUnitId: state.targetUnit?.value,
      targetValue: state.targetValue,
      targetBudgetCost: state.targetBudgetCost,
      targetCurrencyCode: state.targetCurrencyCode?.value as unknown as string,
      dueAt: state.dueAt?.toISOString() || null,
      boardVisibility: state.boardVisibility?.value,
      autoPauseOnL3Plus: state.autoPauseOnL3Plus,
      horizon: state.horizon?.horizonType
        ? {
            horizonType: state.horizon.horizonType?.value,
            schedulerRunFreqMin: state.horizon?.schedulerRunFreqMin,
            replenishThreshold: state.horizon?.replenishThreshold,
            maxQueueSize: state.horizon?.maxQueueSize,
          }
        : null,
      phases: state.phases?.map((phase) => ({
        name: phase.name,
        sequence: phase.sequence,
        executableType: phase.executableType?.value,
        advanceRule: phase.advanceRule?.value,
        workflowTemplateId: phase.workflowTemplate?.value,
        processTemplateId: phase.processTemplate?.value,
        taskCycleTemplateId: phase.taskCycleTemplate?.value,
        bindingConfig: formatBindingConfigForRequest(phase),
      })),
    }

    try {
      const resp: AxiosResponse = await baseUrl.patch(`aim-operations/plan/${id}`, req)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const operationPlansSlice = createSlice({
  name: 'operationPlans',
  initialState,
  reducers: {
    clearOperationPlans: (state) => {
      state.data = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearSingleOperationPlan: (state) => {
      state.singlePlan = null
    },
  },
  extraReducers: {
    [getOperationPlans.pending.type]: (state) => {
      state.isLoading = true
    },
    [getOperationPlans.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllOperationPlansSingleResponse> },
    ) => {
      state.isLoading = false
      state.data = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getOperationPlans.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getOperationPlanDashboard.pending.type]: (state) => {
      state.isLoading = true
    },
    [getOperationPlanDashboard.fulfilled.type]: (
      state,
      { payload }: { payload: IGetSingleOperationPlanDashboardResponse },
    ) => {
      state.isLoading = false
      state.dashboard = payload
    },
    [getOperationPlanDashboard.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleOperationPlan.fulfilled.type]: (
      state,
      { payload }: { payload: IOperationPlanState },
    ) => {
      state.singlePlan = payload
      state.isLoading = false
    },
    [getSingleOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [addOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan added successfully')
    },
    [addOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      console.log(payload)
      handleErrors(payload)
    },
    [editOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [editOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan edited successfully')
    },
    [editOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [activateOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [activateOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan activated successfully')
    },
    [activateOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [cancelOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [cancelOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan cancelled successfully')
    },
    [cancelOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [pauseOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [pauseOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan paused successfully')
    },
    [pauseOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [completeOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [completeOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan completed successfully')
    },
    [completeOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [resumeOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [resumeOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan resumed successfully')
    },
    [resumeOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [activateOperationPlanPhase.pending.type]: (state) => {
      state.isLoading = true
    },
    [activateOperationPlanPhase.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Phase activated successfully')
    },
    [activateOperationPlanPhase.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [completeOperationPlanPhase.pending.type]: (state) => {
      state.isLoading = true
    },
    [completeOperationPlanPhase.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Phase completed successfully')
    },
    [completeOperationPlanPhase.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [skipOperationPlanPhase.pending.type]: (state) => {
      state.isLoading = true
    },
    [skipOperationPlanPhase.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Phase skipped successfully')
    },
    [skipOperationPlanPhase.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearOperationPlans, clearSingleOperationPlan } = operationPlansSlice.actions

export default operationPlansSlice.reducer
